import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import {
  EnvelopeSimple,
  ArrowsClockwise,
  MagnifyingGlass,
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Clock,
  Warning,
  Info,
  CaretDown,
} from "@phosphor-icons/react";

const KIND_ORDER = ["all", "Welcome", "Weekly reminder", "Weekly digest", "Password reset", "Bug report", "Admin reset"];

function StatusBadge({ status }) {
  const map = {
    sent: { bg: "bg-emerald-500/10", text: "text-emerald-700 dark:text-emerald-400", icon: CheckCircle, label: "Sent" },
    failed: { bg: "bg-rose-500/10", text: "text-rose-600 dark:text-rose-400", icon: XCircle, label: "Failed" },
    pending: { bg: "bg-amber-500/10", text: "text-amber-700 dark:text-amber-400", icon: Clock, label: "Pending" },
    suppressed: { bg: "bg-slate-500/10", text: "text-slate-600 dark:text-slate-400", icon: Warning, label: "Suppressed" },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.bg} ${s.text}`}>
      <Icon weight="fill" className="h-3 w-3" />
      {s.label}
    </span>
  );
}

function fmtWhen(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function EmailLogCard() {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(""); // "" | "sent" | "failed" | "pending"
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");
  const [retrying, setRetrying] = useState(null); // log_id currently resending
  // Collapsed by default — the recent-sends table is dense, especially
  // on mobile. KPI tiles stay visible so admin can spot a deliverability
  // dip without expanding.
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (kind && kind !== "all") params.kind = kind;
      if (search.trim()) params.search = search.trim();
      params.limit = 50;
      const [list, s, cfg] = await Promise.all([
        api.get("/admin/email-log", { params }),
        api.get("/admin/email-log/stats"),
        api.get("/admin/email-config").catch(() => ({ data: null })),
      ]);
      setRows(list.data);
      setStats(s.data);
      if (cfg.data) setConfig(cfg.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load email log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, kind]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const onResend = async (row) => {
    if (!window.confirm(`Resend this email to ${row.to}?`)) return;
    setRetrying(row.id);
    try {
      const { data } = await api.post(`/admin/email-log/${row.id}/resend`);
      toast[data.ok ? "success" : "error"](
        data.ok ? `Resent to ${row.to}` : `Resend failed — check the log`,
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Resend failed");
    } finally {
      setRetrying(null);
    }
  };

  return (
    <section data-testid="admin-email-log-card">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
            <EnvelopeSimple weight="fill" className="h-3.5 w-3.5" />
            Email deliverability
          </div>
          <h2 className="font-display font-semibold text-xl tracking-tight mt-1">
            Recent transactional sends
          </h2>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50"
          aria-label="Refresh email log"
          data-testid="email-log-refresh"
        >
          <ArrowsClockwise weight="duotone" className={"h-4 w-4 " + (loading ? "animate-spin" : "")} />
        </button>
      </div>

      {config && <ConfigBanner config={config} failedCount={stats?.failed ?? 0} />}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
          <KpiTile label="Last 7 days" value={stats.total} testId="emlog-kpi-total" />
          <KpiTile label="Sent" value={stats.sent} tone="emerald" testId="emlog-kpi-sent" />
          <KpiTile label="Failed" value={stats.failed} tone="rose" testId="emlog-kpi-failed" />
          <KpiTile
            label="Success rate"
            value={`${stats.success_rate}%`}
            tone={stats.success_rate >= 95 ? "emerald" : stats.success_rate >= 80 ? "amber" : "rose"}
            testId="emlog-kpi-rate"
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 mb-3 px-4 py-3 rounded-2xl border border-border bg-card hover:bg-muted/30 transition group"
        aria-expanded={expanded}
        data-testid="emlog-toggle"
      >
        <span className="text-sm font-medium">
          {expanded ? "Hide recent sends log" : "Show recent sends log"}
          {!expanded && rows.length > 0 && (
            <span className="ml-2 text-xs text-muted-foreground">({rows.length} loaded)</span>
          )}
        </span>
        <CaretDown weight="bold" className={"h-4 w-4 text-primary transition-transform " + (expanded ? "rotate-180" : "")} />
      </button>

      {expanded && (
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full border border-border bg-background p-0.5" data-testid="emlog-status-toggle">
            {[
              { k: "", label: "All" },
              { k: "sent", label: "Sent" },
              { k: "failed", label: "Failed" },
              { k: "pending", label: "Pending" },
            ].map((s) => (
              <button
                key={s.k || "all"}
                type="button"
                onClick={() => setStatus(s.k)}
                className={
                  "h-8 px-3 rounded-full text-xs font-medium transition " +
                  (status === s.k
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground")
                }
                data-testid={`emlog-status-${s.k || "all"}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            data-testid="emlog-kind-select"
          >
            {KIND_ORDER.map((k) => (
              <option key={k} value={k}>{k === "all" ? "All kinds" : k}</option>
            ))}
          </select>

          <form onSubmit={onSearchSubmit} className="flex items-center gap-2 ml-auto">
            <div className="relative">
              <MagnifyingGlass className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search recipient / subject"
                className="h-8 text-xs pl-8 w-56"
                data-testid="emlog-search"
              />
            </div>
            <Button type="submit" variant="ghost" size="sm" className="h-8 text-xs">Go</Button>
          </form>
        </div>

        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/30">
              <tr>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">To</th>
                <th className="text-left px-3 py-2">Subject</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground text-xs">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground text-xs">
                    No matching sends in the log yet.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-border"
                    data-testid={`emlog-row-${r.id}`}
                  >
                    <td className="px-3 py-2">
                      <StatusBadge status={r.status} />
                      {r.status === "failed" && r.error && (
                        <details className="group mt-0.5 max-w-[260px]">
                          <summary className="text-[10px] text-rose-500 cursor-pointer list-none truncate group-open:whitespace-normal group-open:text-rose-600">
                            {r.error}
                          </summary>
                          <div
                            className="mt-1 p-2 rounded bg-rose-50 dark:bg-rose-950/30 text-[10px] text-rose-700 dark:text-rose-300 whitespace-pre-wrap break-words"
                            data-testid={`emlog-error-full-${r.id}`}
                          >
                            {r.error}
                          </div>
                        </details>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground text-xs whitespace-nowrap">
                      {fmtWhen(r.created_at)}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.kind}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[160px]" title={r.to}>{r.to}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[240px]" title={r.subject}>
                      {r.subject}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => onResend(r)}
                        disabled={retrying === r.id}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-primary hover:bg-primary/10 disabled:opacity-50"
                        data-testid={`emlog-resend-${r.id}`}
                      >
                        <PaperPlaneTilt weight="duotone" className="h-3.5 w-3.5" />
                        {retrying === r.id ? "Resending…" : "Resend"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </section>
  );
}

function ConfigBanner({ config, failedCount }) {
  // The banner's job is to tell you if the CONFIG is wrong — not to nag
  // about historical failures. Failed counts are already surfaced in the
  // KPI tile below. Keeping the banner gated on config-only means it goes
  // green the moment you fix env vars + redeploy, without waiting 7 days
  // for old failures to age out.
  const expectedDomain = "thetrucktrackr.com";
  const domainMatch = (config.sender_domain || "").toLowerCase() === expectedDomain;
  const keyPresent = config.resend_api_key_present;
  const configOk = keyPresent && domainMatch;

  if (configOk) {
    return (
      <div
        className="rounded-xl border border-border bg-muted/30 px-3 py-2 mb-3 flex items-center gap-2 text-xs text-muted-foreground"
        data-testid="email-config-banner-ok"
      >
        <Info weight="duotone" className="h-4 w-4 text-primary flex-none" />
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          <span>Sender: <span className="font-mono text-foreground">{config.sender_email}</span></span>
          <span>API key: <span className="font-mono text-foreground">{config.resend_api_key_preview}</span></span>
          {failedCount > 0 && (
            <span className="text-muted-foreground">
              · {failedCount} historical failure{failedCount === 1 ? "" : "s"} in the last 7 days (see table below — retry from there if needed)
            </span>
          )}
        </div>
      </div>
    );
  }

  // Config is actually wrong — highlight each specific problem.
  const problems = [];
  if (!keyPresent) problems.push("RESEND_API_KEY is missing");
  if (!config.sender_email) problems.push("SENDER_EMAIL is missing");
  else if (!domainMatch) {
    problems.push(`Sender domain is "${config.sender_domain}" — expected "${expectedDomain}"`);
  }

  return (
    <div
      className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 mb-3"
      data-testid="email-config-banner-warn"
    >
      <div className="flex items-start gap-2">
        <Warning weight="fill" className="h-5 w-5 text-rose-600 flex-none mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-rose-700 dark:text-rose-400">
            Outgoing email is degraded
          </div>
          <ul className="mt-1 text-xs text-rose-800 dark:text-rose-300 space-y-0.5 list-disc list-inside">
            {problems.map((p) => (<li key={p}>{p}</li>))}
          </ul>
          <div className="mt-2 text-[11px] text-muted-foreground font-mono">
            Current: {config.sender_email || "(unset)"} · key {config.resend_api_key_preview}
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            Fix: update <span className="font-mono">RESEND_API_KEY</span> and <span className="font-mono">SENDER_EMAIL</span> in your deployment&apos;s environment variables, then redeploy.
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, tone = "neutral", testId }) {  const toneMap = {
    neutral: "text-foreground",
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    rose: "text-rose-600 dark:text-rose-400",
  };
  return (
    <div className="rounded-xl border border-border bg-background/50 p-3" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display font-bold text-2xl tabular-nums mt-1 ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}
